// @ts-strict-ignore
import { EventEmitter, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

@Injectable({ providedIn: 'root' })
export class WindowService {

  constructor(
    private bsModalService: BsModalService,
  ) {
    // save and return focus to active element after hide modals
    let activeElement: HTMLElement;
    bsModalService.onShow.subscribe(() => {
      if (this.getModalsCount() === 1) {
        activeElement = (document.activeElement as HTMLElement) || null;
      }
    });
    bsModalService.onHide.subscribe(() => {
      if (this.getModalsCount() === 0) {
        if (activeElement) {
          activeElement.focus();
        }
      }
    });

    // Allow closing a modal by pressing ESC
    bsModalService.config.keyboard = true;
    // Allow closing a modal by clicking outside of it
    bsModalService.config.ignoreBackdropClick = false;
  }

  public getModalsCount(): number {
    return this.bsModalService.getModalsCount();
  }

  /**
   * Uses the finish event of a component to hide it and return a result.
   */
  public async showModalFzFinish<TComponent extends FinishedComponent<TEventArgs>, TEventArgs>(
    content: { new(...args: any[]): TComponent & FinishedComponent<TEventArgs> },
    initialState?: Partial<TComponent>): Promise<TEventArgs> {
    return this.showModalFinish(content, {class: 'modal-fz', initialState,});
  }

  public showModalFz<T>(content: {new (...args: any[]): T }, initialState?: Partial<T>) {
    this.showModal(content, {class: 'modal-fz', initialState,});
  }

  public showModalFullscreen<T>(content: { new(...args: any[]): T }, initialState?: Partial<T>): BsModalRef<T> {
    return this.showModal(content, {class: 'modal-fz fullscreen', initialState});
  }

  /**
   * Uses the finish event of a component to hide it and return a result.
   */
  public async showModalMdFinish<TComponent extends FinishedComponent<TEventArgs>, TEventArgs>(
    content: { new(...args: any[]): TComponent & FinishedComponent<TEventArgs> },
    initialState?: Partial<TComponent>): Promise<TEventArgs> {
    return this.showModalFinish(content, {class: 'modal-md', initialState,});
  }

  public showModalMd<T>(content: { new(...args: any[]): T }, initialState?: Partial<T>): void {
    this.showModal(content, {class: 'modal-md', initialState,});
  }

  public showModal<TComponent>(
    content: { new(...args: any[]): TComponent },
    config?: ModalOptions<TComponent>): BsModalRef<TComponent> {
    return this.bsModalService.show(content, config);
  }

  /**
   * Opens a fullscreen Modal and uses the finish event of a component to hide it and return a result.
   */
  public async showModalFullscreenFinish<TComponent extends FinishedComponent<TEventArgs>, TEventArgs>(
    content: { new(...args: any[]): TComponent & FinishedComponent<TEventArgs> },
    initialState?: Partial<TComponent>): Promise<TEventArgs> {
    return this.showModalFinish(content, {
      class: 'modal-fz fullscreen',
      initialState,
      keyboard: true,
    });
  }

  /**
   * Uses the finish event of a component to hide it and return a result.
   */
  public async showModalFinish<TComponent extends FinishedComponent<TEventArgs>, TEventArgs>(
    content: { new(...args: any[]): TComponent & FinishedComponent<TEventArgs> },
    config?: ModalOptions<TComponent>): Promise<TEventArgs> {

    const bsModalRef = this.bsModalService.show(content,
      config);
    const result = await firstValueFrom(bsModalRef.content.finish);
    bsModalRef.hide();
    return result;
  }
}


export interface FinishedComponent<TEventArgs> {
  finish: EventEmitter<TEventArgs>;
}

